import {
    centsToFormattedDollars,
    currentPostedDate,
    formattedDollarsToCents,
    sanitizeName,
} from '@fruitful/fruitils';
import { addDays } from 'date-fns';

import { type BankAccountDisplayDetail } from '../../banking/types/BankAccount';
import {
    type Card,
    CARDHOLDER_NAME_MAX_LENGTH,
    CardPaymentStatus,
    CardStatus,
} from '../../banking/types/Card';

export const getCardPaymentStatus = ({
    autoPayEnabled,
    paymentDueDate,
    balanceDue,
    daysPastDue,
}: Partial<BankAccountDisplayDetail>): CardPaymentStatus => {
    if (daysPastDue != null && daysPastDue > 0) {
        return CardPaymentStatus.OverdueManualPayment;
    } else if (autoPayEnabled) {
        return CardPaymentStatus.AutoPayEnabled;
    } else if (paymentDueDate != null && balanceDue !== '$0.00') {
        return CardPaymentStatus.UpcomingManualPayment;
    } else {
        return CardPaymentStatus.NoManualPayment;
    }
};

export const daysPastDueDate = (daysPastDue: number): Date => {
    return addDays(currentPostedDate(), -1 * daysPastDue);
};

// cardBalance and pendingTransactions are displayed as negative, convert back by a factor of -1 to display on balance sheets
export const invertNormalizedValue = (value?: string) => {
    const valueCents = formattedDollarsToCents(
        value != null && value !== '$0.00' ? value : '-$0.00',
    );
    return centsToFormattedDollars(valueCents * -1);
};

export const getNonTerminatedCards = (cards?: Card[]) => {
    return cards == null
        ? []
        : cards?.filter((card) => card.cardStatus !== CardStatus.TERMINATED);
};

export const getNonTerminatedCardsForUser = (
    syncteraPersonId: string,
    cards?: Card[],
) => {
    return cards == null
        ? []
        : cards?.filter(
              (card) =>
                  card.cardStatus !== CardStatus.TERMINATED &&
                  card.syncteraPersonId === syncteraPersonId,
          );
};

export const accountHasNonTerminatedCardsFromOtherHolder = (
    syncteraPersonId: string,
    account: BankAccountDisplayDetail,
): boolean => {
    const { jointUser, cards } = account;

    // If there is not a joint user, moot point
    if (jointUser == null) {
        return false;
    }

    return (cards ?? []).some(
        (card) =>
            card.cardStatus !== CardStatus.TERMINATED &&
            card.syncteraPersonId !== syncteraPersonId,
    );
};

/**
 * Strips any characters outside printable ASCII (space through ~) for card embossing,
 * after sanitizing accents and special characters via sanitizeName.
 */
export const toCardFriendlyName = (name: string): string => {
    return sanitizeName(name, true).replace(/[^\x20-\x7E]/g, '');
};

/**
 * See https://support.synctera.com/hc/en-us/requests/27347 for context on how Synctera will derive
 * the emboss name for virtual cards.
 * Derives the emboss-ready full name applying the following rules in order:
 *  1. "FirstName LastName"
 *  2. If #1 exceeds the limit -> "F LastName" (first initial + last name)
 *  3. If #2 exceeds the limit -> "F LastNam" (first initial + truncated last name)
 */
export const truncateCardFriendlyName = (
    cardFriendlyFirstName: string,
    cardFriendlyLastName: string,
): string => {
    const fullName = `${cardFriendlyFirstName} ${cardFriendlyLastName}`;
    if (fullName.length <= CARDHOLDER_NAME_MAX_LENGTH) {
        return fullName;
    }

    const initialName = `${cardFriendlyFirstName.charAt(0)} ${cardFriendlyLastName}`;
    if (initialName.length <= CARDHOLDER_NAME_MAX_LENGTH) {
        return initialName;
    }

    return initialName.substring(0, CARDHOLDER_NAME_MAX_LENGTH);
};
